<template>
  <div class="ur-stat">
    <!-- 篩選列 -->
    <div class="ur-stat-filters">
      <!-- 優先選區 -->
      <span class="ur-stat-filter-label">{{ $t('URBANRENEWAL_ADMIN.STAT.FILTER_ZONE') }}</span>
      <select
        v-model="filters.priority_zone"
        class="ur-stat-select"
        @change="onFilterChange"
      >
        <option value="">{{ $t('URBANRENEWAL_ADMIN.STAT.ALL') }}</option>
        <option v-for="z in priorityOptions" :key="z" :value="z">{{ z }}</option>
      </select>

      <!-- 所有權人姓名 -->
      <input
        v-model="filters.name"
        class="ur-stat-input"
        :placeholder="$t('URBANRENEWAL_ADMIN.STAT.FILTER_NAME')"
        @input="onNameInput"
      />

      <!-- 選配情況 -->
      <span class="ur-stat-filter-label">{{ $t('URBANRENEWAL_ADMIN.STAT.FILTER_STATUS') }}</span>
      <select
        v-model="filters.selection_status"
        class="ur-stat-select"
        @change="onFilterChange"
      >
        <option value="">{{ $t('URBANRENEWAL_ADMIN.STAT.ALL') }}</option>
        <option value="low">{{ $t('URBANRENEWAL_ADMIN.STAT.SEL_LOW') }}</option>
        <option value="normal">{{ $t('URBANRENEWAL_ADMIN.STAT.SEL_NORMAL') }}</option>
        <option value="over">{{ $t('URBANRENEWAL_ADMIN.STAT.SEL_OVER') }}</option>
      </select>

      <!-- 右側：統計數 + 匯出 -->
      <span class="ur-stat-spacer"></span>
      <span class="ur-stat-count">{{ $t('URBANRENEWAL_ADMIN.STAT.COUNT', { count: rows.length }) }}</span>
      <button class="ur-lo-btn" @click="exportData">
        {{ $t('URBANRENEWAL_ADMIN.STAT.EXPORT') }}
      </button>
    </div>

    <!-- 錯誤提示條 -->
    <div v-if="errorMsg" class="ur-lo-error">
      <span>{{ errorMsg }}</span>
      <button class="ur-lo-error-close" @click="errorMsg = ''">&#10007;</button>
    </div>

    <!-- 讀取中 -->
    <div v-if="loading" class="ur-lo-loading">
      {{ $t('URBANRENEWAL_ADMIN.LANDOWNER.LOADING') }}
    </div>

    <!-- 表格 -->
    <div v-else class="ur-table-wrap ur-stat-landowner-wrap">
      <table class="ur-table ur-stat-landowner-table">
        <thead>
          <tr>
            <th>
              {{ $t('URBANRENEWAL_ADMIN.STAT.COL_ZONE') }}
              <button class="ur-stat-sort" :class="{ 'is-active': sortCol === 'priority' }" @click="doSort('priority')">{{ sortIcon('priority') }}</button>
            </th>
            <th>
              {{ $t('URBANRENEWAL_ADMIN.STAT.COL_OWNER') }}
              <button class="ur-stat-sort" :class="{ 'is-active': sortCol === 'owner' }" @click="doSort('owner')">{{ sortIcon('owner') }}</button>
            </th>
            <th>{{ $t('URBANRENEWAL_ADMIN.STAT.COL_HOUSE') }}</th>
            <th>{{ $t('URBANRENEWAL_ADMIN.STAT.COL_PARKING') }}</th>
            <th class="ur-th-num">
              {{ $t('URBANRENEWAL_ADMIN.STAT.COL_SELECTION_TOTAL') }}
              <button class="ur-stat-sort" :class="{ 'is-active': sortCol === 'selection_total' }" @click="doSort('selection_total')">{{ sortIcon('selection_total') }}</button>
            </th>
            <th class="ur-th-num">
              {{ $t('URBANRENEWAL_ADMIN.STAT.COL_MAKEUP') }}
              <button class="ur-stat-sort" :class="{ 'is-active': sortCol === 'makeup' }" @click="doSort('makeup')">{{ sortIcon('makeup') }}</button>
            </th>
            <th>{{ $t('URBANRENEWAL_ADMIN.STAT.COL_MAKEUP_STATUS') }}</th>
            <th>
              {{ $t('URBANRENEWAL_ADMIN.STAT.COL_SELECTION_STATUS') }}
              <button class="ur-stat-sort" :class="{ 'is-active': sortCol === 'status' }" @click="doSort('status')">{{ sortIcon('status') }}</button>
            </th>
            <th class="ur-th-num">
              {{ $t('URBANRENEWAL_ADMIN.STAT.COL_OVER_RATIO') }}
              <button class="ur-stat-sort" :class="{ 'is-active': sortCol === 'ratio' }" @click="doSort('ratio')">{{ sortIcon('ratio') }}</button>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, idx) in pagedRows" :key="idx">
            <td>{{ row.priority_zone || $t('URBANRENEWAL_ADMIN.STAT.DASH') }}</td>
            <td>{{ joinList(row.owner_names) }}</td>
            <td>{{ joinList(row.house_codes) }}</td>
            <td>{{ joinList(row.parking_codes) }}</td>
            <td class="ur-td-num">{{ fmt(row.selection_total) }}</td>
            <td class="ur-td-num">{{ fmt(row.makeup) }}</td>
            <td>
              <span
                v-if="row.makeup_status"
                :class="makeupChip(row.makeup_status)"
              >{{ makeupLabel(row.makeup_status) }}</span>
              <span v-else>{{ $t('URBANRENEWAL_ADMIN.STAT.DASH') }}</span>
            </td>
            <td>
              <span
                v-if="row.selection_status"
                class="ur-stat-status"
                :class="'ur-stat-status-' + row.selection_status"
              >
                <span class="ur-stat-status-dot"></span>
                {{ selectionLabel(row.selection_status) }}
              </span>
              <span v-else>{{ $t('URBANRENEWAL_ADMIN.STAT.DASH') }}</span>
            </td>
            <td class="ur-td-num">
              <span :class="{ 'ur-stat-ratio-over': isOverRatio(row.selection_ratio) }">{{ fmtPct(row.selection_ratio) }}</span>
            </td>
          </tr>
          <tr v-if="rows.length === 0 && !loading">
            <td colspan="9" class="ur-td-empty">
              {{ projectId ? $t('URBANRENEWAL_ADMIN.STAT.NO_DATA') : $t('URBANRENEWAL_ADMIN.STAT.SELECT_PROJECT') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 分頁列（共用元件，設計稿樣式）-->
    <UrbanRenewalPagination
      v-if="!loading && rows.length"
      :total="rows.length"
      :page.sync="page"
      :rpp.sync="rpp"
    />
  </div>
</template>

<script>
import UrbanRenewalDataService from '@/services/UrbanRenewalDataService';
import UrbanRenewalPagination from '@/components/urbanrenewal/UrbanRenewalPagination.vue';

export default {
  name: 'LandownerStatTab',

  components: { UrbanRenewalPagination },

  props: {
    projectId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      rows: [],
      loading: false,
      page: 1,
      rpp: 20,
      sortCol: '',
      sortDir: 'asc',
      filters: {
        priority_zone: '',
        name: '',
        selection_status: '',
      },
      priorityOptions: [],
      errorMsg: '',
      nameTimer: null,
    };
  },

  watch: {
    projectId: {
      immediate: true,
      handler() {
        this.initLoad();
      },
    },
  },

  computed: {
    sortedRows() {
      if (!this.sortCol) return this.rows;
      const dir = this.sortDir === 'asc' ? 1 : -1;
      const col = this.sortCol;
      const val = (row) => {
        if (col === 'priority') return row.priority_zone || '';
        if (col === 'owner') return (row.owner_names || []).join('、');
        if (col === 'selection_total') return row.selection_total || 0;
        if (col === 'makeup') return row.makeup || 0;
        if (col === 'status') return row.selection_status || '';
        if (col === 'ratio') return row.selection_ratio == null ? -Infinity : row.selection_ratio;
        return '';
      };
      return [...this.rows].sort((a, b) => {
        const av = val(a);
        const bv = val(b);
        if (av < bv) return -dir;
        if (av > bv) return dir;
        return 0;
      });
    },
    pagedRows() {
      const start = (this.page - 1) * this.rpp;
      return this.sortedRows.slice(start, start + this.rpp);
    },
  },

  methods: {
    doSort(col) {
      if (this.sortCol === col) {
        if (this.sortDir === 'asc') {
          this.sortDir = 'desc';
        } else {
          this.sortCol = '';
          this.sortDir = 'asc';
        }
      } else {
        this.sortCol = col;
        this.sortDir = 'asc';
      }
      this.page = 1;
    },

    sortIcon(col) {
      if (this.sortCol !== col) return '⇅';
      return this.sortDir === 'asc' ? '↑' : '↓';
    },

    buildParams() {
      const p = {};
      if (this.filters.priority_zone) p.priority_zone = this.filters.priority_zone;
      if (this.filters.name && this.filters.name.trim()) p.name = this.filters.name.trim();
      if (this.filters.selection_status) p.selection_status = this.filters.selection_status;
      return p;
    },

    async fetch(params) {
      if (!this.projectId) {
        this.rows = [];
        return;
      }
      this.loading = true;
      try {
        const res = await UrbanRenewalDataService.getLandownerStatistics(this.projectId, params);
        this.rows = res.data;
        this.page = 1;
      } catch (err) {
        this.errorMsg = this.$t('URBANRENEWAL_ADMIN.STAT.LOAD_FAILED');
      } finally {
        this.loading = false;
      }
    },

    async initLoad() {
      this.filters = { priority_zone: '', name: '', selection_status: '' };
      await this.fetch({});
      this.priorityOptions = [...new Set(
        this.rows.map(r => r.priority_zone).filter(z => z !== null && z !== '')
      )];
    },

    onFilterChange() {
      this.fetch(this.buildParams());
    },

    onNameInput() {
      clearTimeout(this.nameTimer);
      this.nameTimer = setTimeout(() => {
        this.fetch(this.buildParams());
      }, 400);
    },

    async exportData() {
      try {
        const res = await UrbanRenewalDataService.exportLandownerStatistics(this.projectId, this.buildParams());
        const blob = new Blob([res.data], { type: res.headers['content-type'] });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        let fn = '地主統計.xlsx';
        const cd = res.headers['content-disposition'];
        if (cd) {
          const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(cd);
          if (m) fn = decodeURIComponent(m[1]);
        }
        a.download = fn;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        this.errorMsg = this.$t('URBANRENEWAL_ADMIN.STAT.EXPORT_FAILED');
      }
    },

    fmt(v) {
      if (v === null || v === undefined) return this.$t('URBANRENEWAL_ADMIN.STAT.DASH');
      return Number(v).toLocaleString('zh-TW', { maximumFractionDigits: 0 });
    },

    joinList(arr) {
      return arr && arr.length ? arr.join('、') : '-';
    },

    selectionChip(s) {
      return {
        low: 'ur-chip ur-chip-low',
        normal: 'ur-chip ur-chip-normal',
        over: 'ur-chip ur-chip-over',
      }[s] || '';
    },

    selectionLabel(s) {
      const map = {
        low: 'SEL_LOW',
        normal: 'SEL_NORMAL',
        over: 'SEL_OVER',
      };
      return map[s] ? this.$t('URBANRENEWAL_ADMIN.STAT.' + map[s]) : s;
    },

    makeupChip(s) {
      return {
        refund: 'ur-chip ur-chip-refund',
        pay: 'ur-chip ur-chip-pay',
      }[s] || '';
    },

    makeupLabel(s) {
      const map = {
        refund: 'MK_REFUND',
        pay: 'MK_PAY',
      };
      return map[s] ? this.$t('URBANRENEWAL_ADMIN.STAT.' + map[s]) : s;
    },

    fmtPct(v) {
      return v == null ? this.$t('URBANRENEWAL_ADMIN.STAT.DASH') : (v * 100).toFixed(1) + '%';
    },

    isOverRatio(v) {
      return v != null && v > 1;
    },
  },
};
</script>
